import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "field",
    "fieldError",
    "choiceGroup",
    "singleChoiceGroup",
    "eula",
    "eulaError",
    "submit",
    "otherWrap",
    "otherInput",
    "followupWrap",
    "followupOtherWrap",
    "followupOtherInput",
  ]

  validateSubmit(event) {
    let valid = true

    this.fieldTargets.forEach((field) => {
      if (!this.validateDropdown(field)) valid = false
    })

    this.singleChoiceGroupTargets.forEach((group) => {
      if (!this.validateSingleChoice(group)) valid = false
    })

    this.otherInputTargets.forEach((input) => {
      if (!this.validateOtherInput(input)) valid = false
    })

    this.followupOtherInputTargets.forEach((input) => {
      if (!this.validateFollowupOtherInput(input)) valid = false
    })

    this.choiceGroupTargets.forEach((group) => {
      if (!this.validateChoices(group)) valid = false
    })

    if (!this.validateEula()) valid = false

    if (!valid) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }

    document.dispatchEvent(new CustomEvent("auth-loader:show"))
    if (this.hasSubmitTarget) this.submitTarget.disabled = true

    this.element.setAttribute("data-turbo", "false")
  }

  validateDropdown(field) {
    const key = field.dataset.fieldKey
    const message = field.value.trim() ? "" : "This field is required"
    return this.setFieldError(key, message)
  }

  validateSingleChoice(group) {
    const key = group.dataset.fieldKey
    if (key.endsWith("_ad") && !this.followupVisibleFor(key.replace(/_ad$/, ""))) {
      return true
    }

    const checked = group.querySelector('input[type="radio"]:checked')
    const message = checked ? "" : "This field is required"
    return this.setFieldError(key, message)
  }

  validateChoices(group) {
    const key = group.dataset.fieldKey
    const checked = group.querySelectorAll('input[type="checkbox"]:checked')
    const message =
      checked.length > 0 ? "" : "Please select at least one option"
    return this.setFieldError(key, message)
  }

  validateEula() {
    const checkbox = this.hasEulaTarget
      ? this.eulaTarget
      : this.element.querySelector("#licenseagreement")
    const accepted = checkbox?.checked
    if (this.hasEulaErrorTarget) {
      this.eulaErrorTarget.classList.toggle("hidden", accepted)
    }
    return accepted
  }

  choiceChanged(event) {
    const group = event.target.closest(
      '[data-welcome-survey-target="choiceGroup"]',
    )
    if (group) this.setFieldError(group.dataset.fieldKey, "")
  }

  singleChoiceChanged(event) {
    const group = event.target.closest(
      '[data-welcome-survey-target="singleChoiceGroup"]',
    )
    const key = group?.dataset.fieldKey
    if (!key) return

    this.setFieldError(key, "")

    if (key.endsWith("_ad")) {
      const parentKey = key.replace(/_ad$/, "")
      this.toggleFollowupOtherField(parentKey, event.target.value === "Other")
      return
    }

    this.toggleOtherField(key, event.target.value === "Other")
    this.toggleFollowupField(key, event.target.value)
  }

  validateOtherInput(input) {
    const key = input.dataset.fieldKey
    if (this.selectedSourceValue(key) !== "Other") return true

    const message = input.value.trim()
      ? ""
      : "Please tell us where you first heard about Dripshipper"
    return this.setFieldError(`${key}_other`, message)
  }

  validateFollowupOtherInput(input) {
    const key = input.dataset.fieldKey
    if (!this.followupVisibleFor(key)) return true
    if (this.selectedAdValue(key) !== "Other") return true

    const message = input.value.trim() ? "" : "Please tell us more about the ad"
    return this.setFieldError(`${key}_ad_other`, message)
  }

  fieldChanged(event) {
    const field = event.target
    const key = field.dataset.fieldKey
    if (!key) return

    this.setFieldError(key, "")
    this.toggleOtherField(key, field.value === "Other")
    this.toggleFollowupField(key, field.value)
  }

  selectedSourceValue(key) {
    const select = this.fieldTargets.find((f) => f.dataset.fieldKey === key)
    if (select) return select.value

    const group = this.singleChoiceGroupTargets.find(
      (g) => g.dataset.fieldKey === key,
    )
    if (group) {
      const checked = group.querySelector('input[type="radio"]:checked')
      return checked?.value || ""
    }

    return ""
  }

  selectedAdValue(key) {
    const group = this.singleChoiceGroupTargets.find(
      (g) => g.dataset.fieldKey === `${key}_ad`,
    )
    if (!group) return ""

    const checked = group.querySelector('input[type="radio"]:checked')
    return checked?.value || ""
  }

  followupVisibleFor(key) {
    const wrap = this.followupWrapTargets.find(
      (el) => el.dataset.fieldKey === key,
    )
    return wrap && !wrap.hidden
  }

  toggleOtherField(key, show) {
    const wrap = this.otherWrapTargets.find(
      (el) => el.dataset.fieldKey === key,
    )
    if (!wrap) return

    wrap.hidden = !show
    wrap.classList.toggle("hidden", !show)
    if (!show) {
      const input = this.otherInputTargets.find(
        (el) => el.dataset.fieldKey === key,
      )
      if (input) input.value = ""
      this.setFieldError(`${key}_other`, "")
    }
  }

  toggleFollowupField(key, value) {
    const wrap = this.followupWrapTargets.find(
      (el) => el.dataset.fieldKey === key,
    )
    if (!wrap) return

    const show = value === wrap.dataset.followupWhen
    wrap.hidden = !show
    wrap.classList.toggle("hidden", !show)

    if (!show) {
      const adGroup = this.singleChoiceGroupTargets.find(
        (g) => g.dataset.fieldKey === `${key}_ad`,
      )
      adGroup?.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.checked = false
      })
      this.toggleFollowupOtherField(key, false)
      this.setFieldError(`${key}_ad`, "")
      this.setFieldError(`${key}_ad_other`, "")
    }
  }

  toggleFollowupOtherField(key, show) {
    const wrap = this.followupOtherWrapTargets.find(
      (el) => el.dataset.fieldKey === key,
    )
    if (!wrap) return

    wrap.hidden = !show
    wrap.classList.toggle("hidden", !show)
    if (!show) {
      const input = this.followupOtherInputTargets.find(
        (el) => el.dataset.fieldKey === key,
      )
      if (input) input.value = ""
      this.setFieldError(`${key}_ad_other`, "")
    }
  }

  otherInputChanged(event) {
    const key = event.target.dataset.fieldKey
    if (key) this.setFieldError(`${key}_other`, "")
  }

  followupOtherInputChanged(event) {
    const key = event.target.dataset.fieldKey
    if (key) this.setFieldError(`${key}_ad_other`, "")
  }

  clearEulaError() {
    if (this.hasEulaErrorTarget) {
      this.eulaErrorTarget.classList.add("hidden")
    }
  }

  setFieldError(key, message) {
    const errorEl = this.fieldErrorTargets.find(
      (el) => el.dataset.fieldKey === key,
    )
    if (errorEl) {
      errorEl.textContent = message
      errorEl.classList.toggle("hidden", !message)
      errorEl.hidden = !message
    }
    return !message
  }
}
